/**
 * Herkunft einer Anfrage festhalten: Google-Klick-IDs und UTM-Parameter.
 *
 * WOZU: Google Ads kann eine Anfrage nur dann einem Klick zuordnen, wenn
 * die Klick-ID aus der Landeadresse bis zum Absenden des Formulars
 * überlebt. Ohne sie steht im Bericht „Conversion", aber nicht, welche
 * Anzeige sie ausgelöst hat.
 *
 * EINWILLIGUNG: Das Cookie wird NUR geschrieben, wenn Marketing
 * zugestimmt wurde (entspricht ad_storage im Consent Mode v2).
 *
 * Damit die Zuordnung trotzdem nicht verloren geht, wenn jemand erst nach
 * dem Lesen des Banners zustimmt, liegt der Wert bis dahin in einer
 * Variablen im Arbeitsspeicher. Das ist keine Speicherung auf dem Gerät
 * und damit nicht einwilligungspflichtig – sobald die Zustimmung kommt,
 * wandert er ins Cookie, sonst ist er mit dem Schließen des Tabs weg.
 */

import { readConsent } from "@/lib/consent";

export const KLICK_COOKIE = "wedev-klick";
const TAGE = 90;

export type KlickHerkunft = {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  /** Adresse, auf der der Besucher angekommen ist. */
  landung?: string;
  /** Zeitpunkt der Erfassung, ISO. */
  erfasst?: string;
};

const FELDER = ["gclid", "gbraid", "wbraid", "utm_source", "utm_medium", "utm_campaign"] as const;

/** Nur solange der Tab offen ist – keine Speicherung auf dem Gerät. */
let imSpeicher: KlickHerkunft | null = null;

function saeubern(wert: string | null): string | undefined {
  if (!wert) return undefined;
  // Klick-IDs sind alphanumerisch mit - und _; alles andere ist Unfug oder Angriff.
  const sauber = wert.trim().slice(0, 200).replace(/[^\w.-]/g, "");
  return sauber || undefined;
}

function lesenAusUrl(): KlickHerkunft | null {
  if (typeof window === "undefined") return null;
  const p = new URLSearchParams(window.location.search);
  const h: KlickHerkunft = {};
  for (const f of FELDER) {
    const v = saeubern(p.get(f));
    if (v) h[f] = v;
  }
  if (Object.keys(h).length === 0) return null;
  h.landung = window.location.pathname.slice(0, 200);
  h.erfasst = new Date().toISOString();
  return h;
}

function cookieLesen(): KlickHerkunft | null {
  if (typeof document === "undefined") return null;
  const treffer = document.cookie.split("; ").find((c) => c.startsWith(`${KLICK_COOKIE}=`));
  if (!treffer) return null;
  try {
    return JSON.parse(decodeURIComponent(treffer.slice(KLICK_COOKIE.length + 1))) as KlickHerkunft;
  } catch {
    return null;
  }
}

function cookieSchreiben(h: KlickHerkunft) {
  if (typeof document === "undefined") return;
  const wert = encodeURIComponent(JSON.stringify(h));
  const ablauf = new Date(Date.now() + TAGE * 86_400_000).toUTCString();
  // SameSite=Lax: Das Cookie wird beim Klick aus der Anzeige mitgeschickt,
  // aber nicht bei fremden Einbettungen. Secure, weil die Seite nur über
  // HTTPS läuft.
  document.cookie = `${KLICK_COOKIE}=${wert}; expires=${ablauf}; path=/; SameSite=Lax; Secure`;
}

export function marketingErlaubt(): boolean {
  return readConsent()?.marketing === true;
}

/**
 * Beim Seitenaufruf aufrufen. Nimmt die Werte aus der Adresse auf und
 * legt sie ab – im Cookie, wenn eingewilligt wurde, sonst nur im
 * Arbeitsspeicher.
 */
export function klickIdErfassen(): void {
  const ausUrl = lesenAusUrl();
  if (ausUrl) {
    imSpeicher = ausUrl;
    if (marketingErlaubt()) cookieSchreiben(ausUrl);
    return;
  }
  // Keine neuen Werte in der Adresse – vorhandenes Cookie gilt weiter.
  if (!imSpeicher) imSpeicher = cookieLesen();
}

/**
 * Nachträglich ins Cookie schreiben, sobald die Einwilligung erteilt wird.
 * Ohne diesen Schritt ginge die Zuordnung bei jedem verloren, der das
 * Banner erst liest und dann zustimmt – also bei fast allen.
 */
export function klickIdNachEinwilligung(): void {
  if (imSpeicher && marketingErlaubt()) cookieSchreiben(imSpeicher);
}

/** Was dem Formular mitgegeben wird. Leeres Objekt, wenn nichts bekannt ist. */
export function klickHerkunft(): KlickHerkunft {
  return imSpeicher ?? cookieLesen() ?? {};
}
